import { getChannelPageData } from '@/services';
import { ChannelTab } from '@/types/channel';
import { logger } from '@/utils/devUtils';
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { ChannelModule, ModuleFocusInfo } from '@/pages/channel/types/types';

export enum ChannelModuleType {
  RichBanner = 'rich-banner',
  NormalSlider = 'normal-slider',
  ContinueWatching = 'continue-watching',
  HotRank = 'hot-rank',
}

export type TransformedChannel = {
  channelId: string;
  channelType: string;
  title: string;
  path: string;
  filterPageParam: Record<string, string>;
};

type ChannelState = {
  /** 频道页状态 */
  UIState: 'idle' | 'active';
  /** 在idle状态是否更新ui */
  idleUpdateUI: boolean;
  /** 频道页模块 */
  channelModules: ChannelModule[];
  /** 频道页焦点信息 */
  channelFocusInfo: {
    [key: string]: ModuleFocusInfo;
  };
  /** 获焦模块index */
  focusedIndex: number;
  /** 频道id */
  channelId: string;
  /** 频道类型 */
  channelType: number;
  /** 是否还有下一页 */
  hasNextPage: boolean;
  /** 分页数据 */
  pageCtx: string;
  /** 频道列表 */
  channelList: TransformedChannel[];
  /** rich banner color */
  richBannerColor: { r: number; g: number; b: number } | null;
  /** 频道页loading状态 */
  wholePageLoading: 'init' | 'loading' | 'success' | 'error';
  /** 加载更多loading状态 */
  loadMoreLoading: 'init' | 'loading' | 'success' | 'error';
  needResetFocus: boolean;
};

type ChannelAction = {
  /** 设置频道页状态 */
  setUIState: (state: 'idle' | 'active') => void;
  /** 设置是否在idle状态更新ui */
  setIdleUpdateUI: (idleUpdateUI: boolean) => void;
  /** 设置频道模块列表 */
  setChannelModules: (channelModules: ChannelModule[]) => void;
  /** 设置频道页焦点信息 */
  setChannelFocusInfo: (channelFocusInfo: ChannelState['channelFocusInfo']) => void;
  /** 设置某一个模块的聚焦信息 */
  setOneModuleFocusInfo: (midx: string, focusInfo: ModuleFocusInfo) => void;
  /** 设置频道id */
  setChannelId: (channelId: string) => void;
  /** 设置频道类型 */
  setChannelType: (channelType: number) => void;
  /** 设置获焦index */
  setFocusedIndex: (focusedIndex: number) => void;
  /** 设置是否还有下一页 */
  setHasNextPage: (hasNextPage: boolean) => void;
  /** 设置分页 */
  setPageCtx: (pageCtx: string) => void;
  /** 更新频道列表 */
  updateChannelList: () => Promise<void>;
  /** 设置rich banner color */
  setRichBannerColor: (richBannerColor: { r: number; g: number; b: number } | null) => void;
  /** 设置频道页loading状态 */
  setWholePageLoading: (loading: 'init' | 'loading' | 'success' | 'error') => void;
  /** 设置加载更多loading状态 */
  setLoadMoreLoading: (loading: 'init' | 'loading' | 'success' | 'error') => void;
  setNeedResetFocus: (needResetFocus: boolean) => void;
};

const initialState: ChannelState = {
  UIState: 'idle',
  idleUpdateUI: true,
  channelModules: [],
  channelFocusInfo: {},
  focusedIndex: 0,
  channelList: [],
  channelId: '',
  channelType: 0,
  hasNextPage: true,
  pageCtx: '',
  richBannerColor: null,
  wholePageLoading: 'init',
  loadMoreLoading: 'init',
  needResetFocus: false,
};

export const useChannelStore = create<ChannelState & ChannelAction>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,
        setUIState: (state) => {
          set({ UIState: state });
        },
        setNeedResetFocus: (needResetFocus) => {
          set({ needResetFocus });
        },
        setIdleUpdateUI: (idleUpdateUI) => {
          set({ idleUpdateUI });
        },
        setChannelModules: (channelModules) => {
          set({ channelModules });
        },
        setChannelFocusInfo: (channelFocusInfo) => {
          set({ channelFocusInfo });
        },
        setOneModuleFocusInfo: (midx, focusInfo) => {
          set({ channelFocusInfo: { ...get().channelFocusInfo, [midx]: focusInfo } });
        },
        setChannelId: (channelId) => {
          set({ channelId });
        },
        setChannelType: (channelType) => {
          set({ channelType });
        },
        setFocusedIndex: (focusedIndex) => {
          set({ focusedIndex });
        },
        setHasNextPage: (hasNextPage) => {
          set({ hasNextPage });
        },
        setPageCtx: (pageCtx) => {
          set({ pageCtx });
        },
        setWholePageLoading: (loading) => {
          set({ wholePageLoading: loading });
        },
        setLoadMoreLoading: (loading) => {
          set({ loadMoreLoading: loading });
        },
        setRichBannerColor: (richBannerColor) => {
          set({ richBannerColor });
        },
        updateChannelList: async () => {
          try {
            const res = await getChannelPageData({ channel_id: '0', channel_type: 0 });
            set({
              channelList: transformChannelList(res.channel_list?.tabs),
            });
          } catch (err) {
            logger.error('更新频道列表出错', err);
          }
        },
      }),
      {
        name: 'channelList',
        partialize(state) {
          return {
            channelList: state.channelList,
          };
        },
      }
    ),

    { name: 'channel-store', enabled: process.env.NODE_ENV === 'development' }
  )
);

/**
 * 转换频道列表
 */
const transformChannelList = (channelList?: ChannelTab[]) => {
  if (!channelList) {
    return [];
  }
  const transformedList: TransformedChannel[] = [];
  channelList.forEach((item) => {
    transformedList.push({
      channelId: item.channel_id,
      channelType: item.channel_type,
      title: item.title || item.title_en,
      path: `${item.channel_id}/${item.channel_type}`,
      filterPageParam: item.filter_page_param,
    });
  });
  return transformedList;
};
